import { isUserFirefox } from './firefoxUtils';

// VI-702: derive a recording's true duration from the blob so the Pick Your
// Answer thumbnail badge matches the native player exactly (they previously
// diverged — thumbnail used a rounded wall-clock time, the player used the media
// duration, e.g. 00:15 vs 00:14).
//
// MediaRecorder WebM blobs omit the duration header, so on those the element
// reports Infinity until we seek to the end to force resolution.
//
// Rounding is browser-gated on purpose: Firefox's native <video> control rounds
// the displayed total time to the nearest second, so flooring here biased the
// badge low (a 6.7s clip showed 00:06 while the player showed 0:07 — the QA
// mismatch). We round ONLY on Firefox to match it; every other browser keeps the
// original floor behavior so we don't risk introducing the reverse off-by-one on
// players that floor. Falls back to `fallbackSeconds` (same rounding) if it can't
// resolve. See firefoxUtils.isUserFirefox.
export const getMediaDuration = (blob: Blob, fallbackSeconds = 0): Promise<number> => {
  const toSeconds = isUserFirefox() ? Math.round : Math.floor;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const isAudio = (blob.type || '').startsWith('audio/');
    const el = document.createElement(isAudio ? 'audio' : 'video') as HTMLMediaElement;
    el.preload = 'metadata';
    el.muted = true;

    let settled = false;
    const finish = (secs: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('error', onError);
      URL.revokeObjectURL(url);
      const ok = Number.isFinite(secs) && secs > 0;
      resolve(toSeconds(ok ? secs : fallbackSeconds));
    };

    const onLoaded = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) {
        finish(el.duration);
      } else {
        // WebM: force the browser to scan to the real end to resolve duration.
        el.currentTime = 1e101;
      }
    };
    const onTime = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) finish(el.duration);
    };
    const onError = () => finish(fallbackSeconds);

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('error', onError);

    // Safety net: never hang the take on a stubborn blob.
    const timer = setTimeout(() => finish(el.duration), 3000);

    el.src = url;
  });
};
